import type { ExerciseDef } from './types'

// ─── 1.3 (added) while loops ──────────────────────────────────────────────────

export const whileLoop: ExerciseDef = {
  starter: `// A while loop repeats as long as its condition is true —
// useful when you don't know in advance how many steps you need.
let savings = 100
let years = 0
while (savings < 200) {
  savings = savings * 1.07     // 7% growth each year
  years++
}
console.log("doubled after", years, "years:", round(savings, 2))

// break jumps out of a loop early; continue skips to the next round.
for (const x of [3, 8, -1, 5, 12]) {
  if (x < 0) continue
  if (x > 10) break
  console.log("using", x)
}
`,
}

export const collatz: ExerciseDef = {
  starter: `// 1. firstIndexAbove(xs, t): the index of the first item greater than t,
//    or -1 if there isn't one. Use a while loop.
function firstIndexAbove(xs: number[], t: number): number {
  return -1
}

// 2. collatzSteps(n): start at n. If it's even, halve it; if odd, make it 3n + 1.
//    Count the steps until you reach 1. (Nobody has proven this always ends!)
function collatzSteps(n: number): number {
  return 0
}

console.log(firstIndexAbove([2, 5, 9, 1], 4), collatzSteps(6))
`,
  solution: `function firstIndexAbove(xs: number[], t: number): number {
  let i = 0
  while (i < xs.length) {
    if (xs[i] > t) return i
    i++
  }
  return -1
}

function collatzSteps(n: number): number {
  let steps = 0
  while (n !== 1) {
    n = n % 2 === 0 ? n / 2 : 3 * n + 1
    steps++
  }
  return steps
}

console.log(firstIndexAbove([2, 5, 9, 1], 4), collatzSteps(6))
`,
  tests: `
test("firstIndexAbove([2, 5, 9, 1], 4) is 1", () => expect(firstIndexAbove([2, 5, 9, 1], 4), "firstIndexAbove").toBe(1))
test("returns -1 when nothing is above", () => expect(firstIndexAbove([1, 2], 10), "firstIndexAbove([1,2], 10)").toBe(-1))
test("collatzSteps(6) is 8  (6→3→10→5→16→8→4→2→1)", () => expect(collatzSteps(6), "collatzSteps(6)").toBe(8))
test("collatzSteps(1) is 0", () => expect(collatzSteps(1), "collatzSteps(1)").toBe(0))
test("collatzSteps(27) is 111", () => expect(collatzSteps(27), "collatzSteps(27)").toBe(111))
`,
}

// ─── 1.5 Text, sets & maps ────────────────────────────────────────────────────

export const strings: ExerciseDef = {
  starter: `const s = "The Little Fox"

console.log(s.length)                 // 14 characters (spaces count)
console.log(s[0], s[s.length - 1])    // first and last character
console.log(s.toLowerCase())          // "the little fox"
console.log(s.slice(4, 10))           // characters 4 up to (not including) 10
console.log(s.includes("Fox"))        // true
console.log(s.indexOf("L"))           // where "L" first appears: 4
console.log(s.split(" "))             // ["The", "Little", "Fox"]
console.log(["a", "b", "c"].join("-"))  // "a-b-c"
console.log("  padded  ".trim())      // "padded"
console.log("banana".replaceAll("a", "o"))

for (const ch of "abc") console.log("letter:", ch)   // strings are loopable
`,
}

export const vowels: ExerciseDef = {
  starter: `// 1. countVowels("Hello World") → 3   (a, e, i, o, u — upper or lower case)
function countVowels(s: string): number {
  return 0
}

// 2. reverseWords("the little fox") → "fox little the"
function reverseWords(sentence: string): string {
  return sentence
}

// 3. capitalise("the little fox") → "The Little Fox"
function capitalise(sentence: string): string {
  return sentence
}

console.log(countVowels("Hello World"), reverseWords("the little fox"), capitalise("the little fox"))
`,
  solution: `function countVowels(s: string): number {
  let n = 0
  for (const ch of s.toLowerCase()) if ("aeiou".includes(ch)) n++
  return n
}

function reverseWords(sentence: string): string {
  return sentence.split(" ").reverse().join(" ")
}

function capitalise(sentence: string): string {
  return sentence
    .split(" ")
    .map(w => w.slice(0, 1).toUpperCase() + w.slice(1))
    .join(" ")
}

console.log(countVowels("Hello World"), reverseWords("the little fox"), capitalise("the little fox"))
`,
  tests: `
test("countVowels('Hello World') is 3", () => expect(countVowels("Hello World"), "countVowels").toBe(3))
test("countVowels counts upper case too", () => expect(countVowels("AEIOU xyz"), "countVowels('AEIOU xyz')").toBe(5))
test("reverseWords flips the order", () => expect(reverseWords("the little fox"), "reverseWords").toBe("fox little the"))
test("capitalise upper-cases each first letter", () => expect(capitalise("the little fox"), "capitalise").toBe("The Little Fox"))
`,
}

export const setsMaps: ExerciseDef = {
  starter: `// A Set holds each value at most once.
const letters = new Set("mississippi")
console.log(letters.size, [...letters])    // 4 unique letters
console.log(letters.has("s"), letters.has("z"))

// A Map stores key → value pairs. Perfect for counting.
const counts = new Map<string, number>()
for (const ch of "mississippi") {
  counts.set(ch, (counts.get(ch) ?? 0) + 1)   // ?? means "or, if it's missing, use 0"
}
console.log(counts)
for (const [ch, n] of counts) console.log(ch, "appears", n, "times")

// A plain object can also act as a dictionary when the keys are strings:
const age: Record<string, number> = { ada: 36, alan: 41 }
age["grace"] = 85
console.log(Object.keys(age), age.grace)
`,
}

export const wordCounts: ExerciseDef = {
  starter: `// 1. wordCounts(text): a Map from each word to how often it appears.
//    Lower-case everything; split on spaces. ("The the cat" → the: 2, cat: 1)
function wordCounts(text: string): Map<string, number> {
  const counts = new Map<string, number>()
  return counts
}

// 2. mostCommon(counts): the [word, count] pair with the highest count.
function mostCommon(counts: Map<string, number>): [string, number] {
  return ["", 0]
}

// 3. vocabulary(text): the sorted list of distinct words (use a Set).
function vocabulary(text: string): string[] {
  return []
}

const c = wordCounts("The cat and the dog and the bird")
console.log(c, mostCommon(c), vocabulary("b a c a b"))
`,
  solution: `function wordCounts(text: string): Map<string, number> {
  const counts = new Map<string, number>()
  for (const w of text.toLowerCase().split(" ")) {
    if (w === "") continue
    counts.set(w, (counts.get(w) ?? 0) + 1)
  }
  return counts
}

function mostCommon(counts: Map<string, number>): [string, number] {
  let best: [string, number] = ["", 0]
  for (const [w, n] of counts) if (n > best[1]) best = [w, n]
  return best
}

function vocabulary(text: string): string[] {
  return [...new Set(text.toLowerCase().split(" ").filter(w => w !== ""))].sort()
}

const c = wordCounts("The cat and the dog and the bird")
console.log(c, mostCommon(c), vocabulary("b a c a b"))
`,
  tests: `
test("counts are case-insensitive", () => expect(wordCounts("The the cat").get("the"), "count of 'the'").toBe(2))
test("each word is counted", () => expect(wordCounts("a b a").get("b"), "count of 'b'").toBe(1))
test("mostCommon finds the top word", () => expect(mostCommon(wordCounts("The cat and the dog and the bird")), "mostCommon").toEqual(["the", 3]))
test("vocabulary is sorted and unique", () => expect(vocabulary("b a c a b"), "vocabulary").toEqual(["a", "b", "c"]))
`,
}

// ─── 1.6 Code power tools ─────────────────────────────────────────────────────

export const destructuring: ExerciseDef = {
  starter: `// Destructuring: unpack arrays and objects into variables in one line.
const point = [3, 4]
const [x, y] = point
console.log(x, y)

const house = { size: 120, rooms: 3, price: 250000 }
const { size, price } = house
console.log(size, price)

// Functions often return several things this way:
function minMax(xs: number[]): [number, number] {
  return [Math.min(...xs), Math.max(...xs)]   // ...xs spreads the array into arguments
}
const [lo, hi] = minMax([4, 9, 1, 7])
console.log(lo, hi)

// Spread also copies and combines arrays and objects:
const a = [1, 2], b = [3, 4]
console.log([...a, ...b, 5])
console.log({ ...house, price: 260000 })   // a copy with one field changed

// Rest parameters collect any number of arguments into an array:
const total = (...nums: number[]) => nums.reduce((s, n) => s + n, 0)
console.log(total(1, 2, 3, 4))
`,
}

export const sorting: ExerciseDef = {
  starter: `// Careful! Without a comparator, sort compares numbers as TEXT:
console.log([10, 9, 1, 100].sort())                  // [1, 10, 100, 9] 😬
console.log([10, 9, 1, 100].sort((a, b) => a - b))   // ascending
console.log([10, 9, 1, 100].sort((a, b) => b - a))   // descending

// Sort objects by a property:
const people = [{ name: "Ada", age: 36 }, { name: "Alan", age: 41 }, { name: "Grace", age: 85 }]
console.log(people.slice().sort((p, q) => q.age - p.age).map(p => p.name))
// .slice() first: sort changes the original array, and we want to keep it.

// The ternary operator is a compact if/else that produces a value:
const temp = 31
console.log(temp > 25 ? "hot" : "not hot")

// The Math toolbox
console.log(Math.floor(2.7), Math.ceil(2.1), Math.round(2.5), Math.abs(-3), Math.sqrt(16), Math.PI)
console.log(17 % 5)   // remainder: 17 = 3×5 + 2
`,
}

export const topK: ExerciseDef = {
  starter: `// 1. topK(scores, k): the indices of the k largest scores, biggest first.
//    topK([0.1, 0.7, 0.05, 0.15], 2) → [1, 3]
//    Hint: make the list of indices with range(scores.length), then sort it.
function topK(scores: number[], k: number): number[] {
  return []
}

// 2. summarise(xs): return an object { min, max, mean }.
function summarise(xs: number[]): { min: number; max: number; mean: number } {
  return { min: 0, max: 0, mean: 0 }
}

// 3. label(p): "likely" if p ≥ 0.5, otherwise "unlikely" — in one line, with a ternary.
const label = (p: number): string => ""

console.log(topK([0.1, 0.7, 0.05, 0.15], 2), summarise([4, 8, 6]), label(0.9))
`,
  solution: `function topK(scores: number[], k: number): number[] {
  return range(scores.length)
    .sort((a, b) => scores[b] - scores[a])
    .slice(0, k)
}

function summarise(xs: number[]): { min: number; max: number; mean: number } {
  const total = xs.reduce((s, x) => s + x, 0)
  return { min: Math.min(...xs), max: Math.max(...xs), mean: total / xs.length }
}

const label = (p: number): string => (p >= 0.5 ? "likely" : "unlikely")

console.log(topK([0.1, 0.7, 0.05, 0.15], 2), summarise([4, 8, 6]), label(0.9))
`,
  tests: `
test("topK picks the two biggest, biggest first", () => expect(topK([0.1, 0.7, 0.05, 0.15], 2), "topK(..., 2)").toEqual([1, 3]))
test("topK(…, 1) is the argmax", () => expect(topK([3, 9, 2, 9.5], 1), "topK(..., 1)").toEqual([3]))
test("topK doesn't change the scores array", () => {
  const s = [5, 1, 3]
  topK(s, 2)
  expect(s, "scores after topK").toEqual([5, 1, 3])
})
test("summarise returns min, max and mean", () => expect(summarise([4, 8, 6]), "summarise([4, 8, 6])").toEqual({ min: 4, max: 8, mean: 6 }))
test("label uses the 0.5 threshold", () => expect([label(0.5), label(0.49)], "[label(0.5), label(0.49)]").toEqual(["likely", "unlikely"]))
`,
}

// ─── 1.7 Closures & recursion ─────────────────────────────────────────────────

export const closuresIntro: ExerciseDef = {
  starter: `// A function can build and return another function.
function makeMultiplier(k: number) {
  return (x: number) => x * k      // this inner function "remembers" k
}
const double = makeMultiplier(2)
const triple = makeMultiplier(3)
console.log(double(10), triple(10))

// That memory is called a closure. It can even hold state that changes:
function makeCounter() {
  let count = 0
  return () => {
    count++
    return count
  }
}
const next = makeCounter()
console.log(next(), next(), next())   // 1 2 3 — count lives on between calls
const other = makeCounter()
console.log(other())                  // 1 — a separate counter with its own count
`,
}

export const closures: ExerciseDef = {
  starter: `// 1. compose(f, g): a new function that runs g, then f:  compose(f, g)(x) = f(g(x))
function compose(f: (x: number) => number, g: (x: number) => number): (x: number) => number {
  return x => x
}

// 2. makeAccumulator(): returns { add, total }. add(n) adds n to a running total;
//    total() reports it. Each accumulator keeps its own total.
function makeAccumulator(): { add: (n: number) => void; total: () => number } {
  return { add: () => {}, total: () => 0 }
}

const inc = (x: number) => x + 1
const sq = (x: number) => x * x
console.log(compose(sq, inc)(3))   // (3 + 1)² = 16
const acc = makeAccumulator()
acc.add(5); acc.add(2)
console.log(acc.total())          // 7
`,
  solution: `function compose(f: (x: number) => number, g: (x: number) => number): (x: number) => number {
  return x => f(g(x))
}

function makeAccumulator(): { add: (n: number) => void; total: () => number } {
  let sum = 0
  return {
    add: (n: number) => {
      sum += n
    },
    total: () => sum,
  }
}

const inc = (x: number) => x + 1
const sq = (x: number) => x * x
console.log(compose(sq, inc)(3))
const acc = makeAccumulator()
acc.add(5); acc.add(2)
console.log(acc.total())
`,
  tests: `
test("compose(sq, inc)(3) is 16", () => expect(compose(x => x * x, x => x + 1)(3), "compose(sq, inc)(3)").toBe(16))
test("compose(inc, sq)(3) is 10 — order matters", () => expect(compose(x => x + 1, x => x * x)(3), "compose(inc, sq)(3)").toBe(10))
test("an accumulator adds things up", () => {
  const a = makeAccumulator()
  a.add(5); a.add(2); a.add(-1)
  expect(a.total(), "total").toBe(6)
})
test("two accumulators don't share a total", () => {
  const a = makeAccumulator(), b = makeAccumulator()
  a.add(10); b.add(1)
  expect([a.total(), b.total()], "[a, b]").toEqual([10, 1])
})
`,
}

export const recursionIntro: ExerciseDef = {
  starter: `// A recursive function calls itself on a smaller version of the problem,
// until it reaches a case so small it can answer directly (the "base case").
function factorial(n: number): number {
  if (n <= 1) return 1              // base case
  return n * factorial(n - 1)       // recursive case: shrink the problem
}
console.log(factorial(5))   // 5 × 4 × 3 × 2 × 1 = 120

// Recursion shines on things that contain smaller versions of themselves,
// like lists inside lists:
function sumNested(x: number | unknown[]): number {
  if (typeof x === "number") return x
  return x.reduce((s: number, item) => s + sumNested(item as number | unknown[]), 0)
}
console.log(sumNested([1, [2, 3], [[4], 5]]))   // 15
`,
}

export const recursion: ExerciseDef = {
  starter: `// A tree: each node has a value and a list of children.
type TreeNode = { value: number; children: TreeNode[] }
const tree: TreeNode = {
  value: 1,
  children: [
    { value: 2, children: [{ value: 4, children: [] }] },
    { value: 3, children: [{ value: 5, children: [] }, { value: 6, children: [] }] },
  ],
}

// 1. treeSum(node): the sum of every value in the tree.
function treeSum(node: TreeNode): number {
  return 0
}

// 2. depth(node): the number of levels. A node with no children has depth 1.
function depth(node: TreeNode): number {
  return 0
}

// 3. childrenFirst(node): every value, children before their parent.
//    For the tree above: [4, 2, 5, 6, 3, 1]. (Autograd walks graphs in exactly this order!)
function childrenFirst(node: TreeNode): number[] {
  return []
}

console.log(treeSum(tree), depth(tree), childrenFirst(tree))
`,
  solution: `type TreeNode = { value: number; children: TreeNode[] }
const tree: TreeNode = {
  value: 1,
  children: [
    { value: 2, children: [{ value: 4, children: [] }] },
    { value: 3, children: [{ value: 5, children: [] }, { value: 6, children: [] }] },
  ],
}

function treeSum(node: TreeNode): number {
  return node.value + node.children.reduce((s, c) => s + treeSum(c), 0)
}

function depth(node: TreeNode): number {
  if (node.children.length === 0) return 1
  return 1 + Math.max(...node.children.map(depth))
}

function childrenFirst(node: TreeNode): number[] {
  const out: number[] = []
  for (const c of node.children) out.push(...childrenFirst(c))
  out.push(node.value)
  return out
}

console.log(treeSum(tree), depth(tree), childrenFirst(tree))
`,
  tests: `
test("treeSum adds every node", () => expect(treeSum(tree), "treeSum(tree)").toBe(21))
test("a leaf has depth 1", () => expect(depth({ value: 7, children: [] }), "depth(leaf)").toBe(1))
test("the example tree has depth 3", () => expect(depth(tree), "depth(tree)").toBe(3))
test("childrenFirst lists children before parents", () => expect(childrenFirst(tree), "childrenFirst(tree)").toEqual([4, 2, 5, 6, 3, 1]))
`,
}

// ─── 1.8 Project: text analyser ───────────────────────────────────────────────

export const textWords: ExerciseDef = {
  starter: `// Part 1 — from raw text to words.
// words(text): lower-case, turn anything that isn't a letter into a space, split, drop empties.
// Tip: text.replace(/[^a-z]+/g, " ") replaces every run of non-letters with one space.
function words(text: string): string[] {
  return []
}

const all = words(data.stories)
console.log("total words:", all.length)
console.log("distinct words:", new Set(all).size)
console.log("first ten:", all.slice(0, 10))
`,
  solution: `function words(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z]+/g, " ")
    .split(" ")
    .filter(w => w !== "")
}

const all = words(data.stories)
console.log("total words:", all.length)
console.log("distinct words:", new Set(all).size)
console.log("first ten:", all.slice(0, 10))
`,
  tests: `
test("splits on spaces and punctuation", () => expect(words("The fox ran. It was fast!"), "words(...)").toEqual(["the", "fox", "ran", "it", "was", "fast"]))
test("drops empty pieces", () => expect(words("  ...  "), "words('  ...  ')").toEqual([]))
test("the stories contain over 500 words", () => expect(words(data.stories).length, "number of words").toBeGreaterThan(500))
`,
}

export const textTop: ExerciseDef = {
  starter: `function words(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z]+/g, " ").split(" ").filter(w => w !== "")
}

// Part 2 — the most frequent words.
// topWords(ws, k): the k most common words as [word, count] pairs, most common first.
// Ties: alphabetical order.
function topWords(ws: string[], k: number): [string, number][] {
  return []
}

const top = topWords(words(data.stories), 12)
console.table(top.map(([word, count]) => ({ word, count })))
plot.bar(Object.fromEntries(top), { title: "most common words" })
`,
  solution: `function words(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z]+/g, " ").split(" ").filter(w => w !== "")
}

function topWords(ws: string[], k: number): [string, number][] {
  const counts = new Map<string, number>()
  for (const w of ws) counts.set(w, (counts.get(w) ?? 0) + 1)
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))
    .slice(0, k)
}

const top = topWords(words(data.stories), 12)
console.table(top.map(([word, count]) => ({ word, count })))
plot.bar(Object.fromEntries(top), { title: "most common words" })
`,
  tests: `
test("counts and orders words", () => expect(topWords(["b", "a", "b", "c", "b", "a"], 2), "topWords(...)").toEqual([["b", 3], ["a", 2]]))
test("ties are alphabetical", () => expect(topWords(["y", "x"], 2), "topWords(['y','x'])").toEqual([["x", 1], ["y", 1]]))
test("'the' is the most common word in the stories", () => expect(topWords(words(data.stories), 1)[0][0], "top word").toBe("the"))
`,
}

export const textNext: ExerciseDef = {
  starter: `function words(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z]+/g, " ").split(" ").filter(w => w !== "")
}

// Part 3 — your first language model!
// followers(ws): for every word, a Map counting which words come right after it.
//   followers(["a", "b", "a", "c"]) → a: {b: 1, c: 1}, b: {a: 1}
function followers(ws: string[]): Map<string, Map<string, number>> {
  const table = new Map<string, Map<string, number>>()
  return table
}

// mostLikelyNext(table, word): the follower seen most often (or "" if none).
function mostLikelyNext(table: Map<string, Map<string, number>>, word: string): string {
  return ""
}

const table = followers(words(data.stories))
let w = "the", sentence = [w]
for (let i = 0; i < 10; i++) {
  w = mostLikelyNext(table, w)
  if (!w) break
  sentence.push(w)
}
console.log(sentence.join(" "))
`,
  solution: `function words(text: string): string[] {
  return text.toLowerCase().replace(/[^a-z]+/g, " ").split(" ").filter(w => w !== "")
}

function followers(ws: string[]): Map<string, Map<string, number>> {
  const table = new Map<string, Map<string, number>>()
  for (let i = 0; i < ws.length - 1; i++) {
    const row = table.get(ws[i]) ?? new Map<string, number>()
    row.set(ws[i + 1], (row.get(ws[i + 1]) ?? 0) + 1)
    table.set(ws[i], row)
  }
  return table
}

function mostLikelyNext(table: Map<string, Map<string, number>>, word: string): string {
  const row = table.get(word)
  if (!row) return ""
  let best = ""
  let bestN = 0
  for (const [next, n] of row) if (n > bestN) [best, bestN] = [next, n]
  return best
}

const table = followers(words(data.stories))
let w = "the", sentence = [w]
for (let i = 0; i < 10; i++) {
  w = mostLikelyNext(table, w)
  if (!w) break
  sentence.push(w)
}
console.log(sentence.join(" "))
`,
  tests: `
test("followers counts each pair", () => {
  const t = followers(["a", "b", "a", "c", "a", "b"])
  expect([t.get("a")?.get("b"), t.get("a")?.get("c"), t.get("b")?.get("a")], "[a→b, a→c, b→a]").toEqual([2, 1, 1])
})
test("mostLikelyNext picks the most frequent follower", () => expect(mostLikelyNext(followers(["a", "b", "a", "c", "a", "b"]), "a"), "next after 'a'").toBe("b"))
test("an unknown word gives an empty string", () => expect(mostLikelyNext(followers(["a", "b"]), "zebra"), "next after 'zebra'").toBe(""))
`,
}
